import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowDownTrayIcon, DocumentIcon, TrashIcon, PlusIcon, XMarkIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../../contexts/ToastContext';
import safetySystemService from '../../../services/safetySystem.service';
import type { SafetyDocument, SafetyDocumentTable } from '../../../types/safetySystem';
import { saveAs } from '../../../utils/saveFile';
import {
  ALLOWED_EXTENSIONS, ALLOWED_EXTENSIONS_SET, ALLOWED_EXTENSIONS_DISPLAY,
  MAX_FILE_SIZE, MAX_FILE_SIZE_DISPLAY, IMAGE_EXTENSIONS,
} from '../../../config/fileTypes';
import ModalHeader from '../../../components/ui/ModalHeader';
import { useModalDrag } from '../../../hooks/useModalDrag';

/**
 * Documents panel for the Safety unit drawer (adapted from RepairOrderDocuments).
 * Lists / uploads / downloads / soft-deletes `safetynew_map` documents for one
 * `tablename` + `tablerecno` pair. Image previews use the absolute blob `url` the API
 * returns; downloads stream through the API so the Save As dialog gets the real name.
 */

export interface SafetyDocumentsProps {
  tablename: SafetyDocumentTable;
  tablerecno: number | null;
  isReadOnly: boolean;
}

const DEFAULT_DOC_TYPE = 'PHOTO';

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const isImage = (filetype: string) => IMAGE_EXTENSIONS.has((filetype || '').toLowerCase());

export default function SafetyDocuments({ tablename, tablerecno, isReadOnly }: SafetyDocumentsProps) {
  const { showToast } = useToast();
  const [documents, setDocuments] = useState<SafetyDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState(DEFAULT_DOC_TYPE);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [lightboxDoc, setLightboxDoc] = useState<SafetyDocument | null>(null);
  const lightboxDrag = useModalDrag(!!lightboxDoc);

  const fetchDocuments = useCallback(async () => {
    if (!tablerecno) { setDocuments([]); setLoading(false); return; }
    setLoading(true);
    try {
      const result = await safetySystemService.listDocuments(tablename, tablerecno);
      setDocuments(result?.data ?? []);
      setLoadFailed(false);
    } catch (err) {
      console.error('Failed to fetch safety documents:', err);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [tablename, tablerecno]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // Datalist for the free-text type: every type already on this record, plus PHOTO.
  const docTypeOptions = useMemo(() => {
    const seen = new Set<string>([DEFAULT_DOC_TYPE]);
    documents.forEach((d) => { if (d.documenttype) seen.add(d.documenttype.toUpperCase()); });
    return Array.from(seen).sort();
  }, [documents]);

  const resetUploadForm = () => {
    setShowUploadForm(false);
    setUploadFile(null);
    setUploadDocType(DEFAULT_DOC_TYPE);
    setUploadDescription('');
  };

  // Called straight from the click handler so the Save As picker keeps the user gesture.
  const handleDownload = (doc: SafetyDocument) => {
    setDownloading(doc.recno);
    saveAs(doc.filename, () => safetySystemService.downloadDocument(doc.recno))
      .catch(() => showToast({ message: 'Failed to download document', type: 'error' }))
      .finally(() => setDownloading(null));
  };

  const handleUpload = async () => {
    if (!uploadFile || !tablerecno) return;
    const ext = uploadFile.name.split('.').pop()?.toLowerCase();
    if (!ext || !ALLOWED_EXTENSIONS_SET.has(ext)) {
      showToast({ message: `File type '.${ext || 'unknown'}' is not supported. Allowed types: ${ALLOWED_EXTENSIONS_DISPLAY}`, type: 'error' });
      return;
    }
    if (uploadFile.size > MAX_FILE_SIZE) {
      const sizeMB = (uploadFile.size / (1024 * 1024)).toFixed(1);
      showToast({ message: `File is too large (${sizeMB}MB). Maximum allowed size is ${MAX_FILE_SIZE_DISPLAY}.`, type: 'error' });
      return;
    }
    const docType = uploadDocType.trim().toUpperCase();
    if (!docType) {
      showToast({ message: 'Document type is required', type: 'error' });
      return;
    }
    setUploading(true);
    try {
      await safetySystemService.uploadDocument(tablename, tablerecno, uploadFile, docType, uploadDescription.trim());
      showToast({ message: 'Document uploaded', type: 'success' });
      resetUploadForm();
      await fetchDocuments();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showToast({ message: typeof detail === 'string' ? detail : 'Failed to upload document', type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (recno: number) => {
    setDeleting(recno);
    try {
      await safetySystemService.deleteDocument(recno);
      showToast({ message: 'Document removed', type: 'success' });
      setConfirmDelete(null);
      await fetchDocuments();
    } catch {
      showToast({ message: 'Failed to remove document', type: 'error' });
    } finally {
      setDeleting(null);
    }
  };

  const fieldClass = 'w-full rounded-md border border-rule-strong bg-field px-3 py-2 text-sm text-heading focus:ring-2 focus:ring-accent';
  const btnDisabled = 'disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400';

  return (
    <>
      <div className="rounded-lg border border-rule bg-panel p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-heading">
            Documents
            {documents.length > 0 && <span className="ml-2 text-xs font-normal text-muted">({documents.length})</span>}
          </h3>
          {!isReadOnly && !showUploadForm && tablerecno && (
            <button
              type="button"
              onClick={() => setShowUploadForm(true)}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-accent-text hover:bg-highlight"
            >
              <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Add Document
            </button>
          )}
        </div>

        {showUploadForm && (
          <div className="mb-4 rounded-lg border border-rule bg-highlight p-3">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-medium text-label">Upload Document</span>
              <button type="button" onClick={resetUploadForm} aria-label="Close upload form" className="rounded p-0.5 text-muted hover:text-heading">
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label htmlFor="safety-doc-file" className="mb-1 block text-xs font-medium text-muted">File</label>
                <input
                  id="safety-doc-file"
                  type="file"
                  accept={ALLOWED_EXTENSIONS}
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-label file:mr-3 file:rounded-md file:border-0 file:bg-accent file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-accent-fg hover:file:bg-accent-hover"
                />
                {uploadFile && <p className="mt-1 text-xs text-muted">{uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB)</p>}
              </div>
              <div>
                <label htmlFor="safety-doc-type" className="mb-1 block text-xs font-medium text-muted">Document Type</label>
                <input
                  id="safety-doc-type"
                  type="text"
                  list="safety-doc-type-options"
                  value={uploadDocType}
                  onChange={(e) => setUploadDocType(e.target.value)}
                  className={fieldClass}
                />
                <datalist id="safety-doc-type-options">
                  {docTypeOptions.map((t) => <option key={t} value={t} />)}
                </datalist>
              </div>
              <div>
                <label htmlFor="safety-doc-description" className="mb-1 block text-xs font-medium text-muted">Description</label>
                <input
                  id="safety-doc-description"
                  type="text"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Optional description..."
                  className={fieldClass}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={resetUploadForm} className="rounded-md border border-rule px-3 py-1.5 text-xs font-medium text-label hover:bg-highlight">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!uploadFile || uploading}
                  className={`rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-accent-fg hover:bg-accent-hover ${btnDisabled}`}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-xs text-muted">Loading documents...</p>
        ) : loadFailed ? (
          <p className="text-xs text-danger-fg">Couldn't load documents — the request failed.</p>
        ) : documents.length === 0 ? (
          <p className="text-xs text-muted">No documents attached.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {documents.map((doc) => (
              <div key={doc.recno} className="relative overflow-hidden rounded-lg border border-rule bg-highlight">
                {isImage(doc.filetype) ? (
                  <button type="button" className="relative block h-32 w-full bg-gray-200 dark:bg-gray-700" onClick={() => setLightboxDoc(doc)} aria-label={`Preview ${doc.filename}`}>
                    <img src={doc.url} alt={doc.filename} className="h-full w-full object-cover" />
                    <span className="absolute bottom-1 right-1 rounded bg-scrim/60 p-0.5 text-white">
                      <MagnifyingGlassPlusIcon className="h-4 w-4" aria-hidden="true" />
                    </span>
                  </button>
                ) : (
                  <button type="button" className="flex h-32 w-full flex-col items-center justify-center bg-gray-100 dark:bg-gray-700" onClick={() => handleDownload(doc)} aria-label={`Open ${doc.filename}`}>
                    <DocumentIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                    <span className="mt-1 text-xs font-medium uppercase text-muted">{doc.filetype || 'FILE'}</span>
                  </button>
                )}

                <div className="p-2">
                  <p className="truncate text-xs font-medium text-heading" title={doc.filename}>{doc.filename}</p>
                  <p className="truncate text-[0.6875rem] text-muted">
                    {doc.documenttype}
                    {doc.description ? ` — ${doc.description}` : ''}
                    {formatDate(doc.uploaddatetime) ? ` · ${formatDate(doc.uploaddatetime)}` : ''}
                  </p>
                </div>

                <div className="absolute right-1 top-1 flex gap-1">
                  <button
                    type="button"
                    onClick={() => handleDownload(doc)}
                    disabled={downloading === doc.recno}
                    className="rounded bg-panel/90 p-1 text-muted shadow-sm hover:text-accent-text"
                    title="Download"
                    aria-label={`Download ${doc.filename}`}
                  >
                    <ArrowDownTrayIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                  {!isReadOnly && (
                    confirmDelete === doc.recno ? (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleDelete(doc.recno)}
                          disabled={deleting === doc.recno}
                          className={`rounded bg-red-600 px-1.5 py-0.5 text-[0.625rem] font-medium text-white shadow-sm hover:bg-red-700 ${btnDisabled}`}
                        >
                          {deleting === doc.recno ? '...' : 'Delete'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(null)}
                          className="rounded bg-panel/90 px-1.5 py-0.5 text-[0.625rem] font-medium text-label shadow-sm hover:bg-highlight"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(doc.recno)}
                        className="rounded bg-panel/90 p-1 text-muted shadow-sm hover:text-red-600"
                        title="Remove"
                        aria-label={`Remove ${doc.filename}`}
                      >
                        <TrashIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {lightboxDoc && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-scrim/80" onClick={() => setLightboxDoc(null)}>
          <div
            ref={lightboxDrag.panelRef}
            style={lightboxDrag.panelStyle}
            className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              title={lightboxDoc.filename}
              onClose={() => setLightboxDoc(null)}
              dragHandleProps={lightboxDrag.dragHandleProps}
              actions={(
                <button
                  type="button"
                  onClick={() => handleDownload(lightboxDoc)}
                  className="rounded bg-white/10 p-1.5 text-slate-300 hover:bg-white/20 hover:text-white"
                  title="Download"
                  aria-label={`Download ${lightboxDoc.filename}`}
                >
                  <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            />
            <img src={lightboxDoc.url} alt={lightboxDoc.filename} className="max-h-[calc(90vh-4rem)] max-w-full object-contain" />
          </div>
        </div>
      )}
    </>
  );
}
